import { PropertyHelper } from "@/helpers/property.helper";
import { UserHelper } from "@/helpers/user.helper";
import { AlertService } from "@/services/alert.service";
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { UntypedFormBuilder } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatMenuModule } from "@angular/material/menu";
import { Photo, PhotosService, RealProperty } from "@eemo/api-client";
import { Subscription } from "rxjs";
import { ConfirmDialogComponent } from "../confirm-dialog/confirm-dialog.component";
import { PhotoUpdateDialogComponent } from "../photo-update-dialog/photo-update-dialog.component";

import { TooltipDirective } from "@/directives/tooltip/tooltip.directive";
import { NgIf } from "@angular/common";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { ReOrderDialogComponent } from "../re-order-dialog/re-order-dialog.component";

@Component({
  selector: "app-property-photo-card",
  templateUrl: "./property-photo-card.component.html",
  styleUrls: ["./property-photo-card.component.scss"],
  standalone: true,
  imports: [
    MatCardModule,
    NgIf,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    TooltipDirective,
    MatCheckboxModule,
  ],
})
export class PropertyPhotoCardComponent implements OnInit, OnDestroy {
  @Input() prop: RealProperty;
  @Input() photo: Photo;
  @Input() order: number;
  @Input() isFirst: boolean;
  @Input() isLast: boolean;
  @Input() enableSelection: boolean;
  @Output() photoUpdated = new EventEmitter<boolean>();
  @Output() mainPhotoUpdated = new EventEmitter<boolean>();
  @Output() photoClick = new EventEmitter<boolean>();
  @Output() photoSelected = new EventEmitter<boolean>();

  canUpdateProperty = false;
  photoSrc: string;
  photoSrcSet: string;
  photoImageUrl: string;
  photoFilename: string;
  photoName: string;

  updateSub = new Subscription();
  deleteSub = new Subscription();
  setMainSub = new Subscription();
  changeOrderSub = new Subscription();

  constructor(
    private photoService: PhotosService,
    private alertService: AlertService,
    public userHelper: UserHelper,
    public propHelper: PropertyHelper,
    private dialog: MatDialog,
    private fb: UntypedFormBuilder
  ) {}

  ngOnInit() {
    this.canUpdateProperty = this.userHelper.canUpdateProperty(this.prop);
    this.photoSrc = this.propHelper.getPhotoImageUrlWithSize(
      this.photo,
      "400x300"
    );
    this.photoSrcSet =
      this.propHelper.getPhotoImageUrlWithSize(this.photo, "800x600") + " 2x";
    this.photoImageUrl = this.propHelper.getPhotoImageUrl(this.photo);
    this.photoFilename = this.propHelper.getPhotoFilename(this.photo);
    // revert the hide of photo name
    /*this.photoName = `Photo ${this.propHelper.getPhotoCategoryName(this.photo.category).toLocaleLowerCase()} n°${
      this.photo.order
    }`;*/
    this.photoName = this.photo.name;
  }

  ngOnDestroy(): void {
    this.updateSub.unsubscribe();
    this.deleteSub.unsubscribe();
    this.setMainSub.unsubscribe();
    this.changeOrderSub.unsubscribe();
  }

  selectPhotoChanged($event) {
    this.photoSelected.emit($event.checked);
  }

  onPhotoClick() {
    this.photoClick.emit(true);
  }

  downloadPhoto() {
    window.location.href = this.propHelper.getPhotoImageUrl(this.photo);
  }

  editPhoto() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PhotoUpdateDialogComponent, {
        data: { photo: this.photo },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.photoUpdated.emit(true);
        }
      });
    }
  }

  confirmDeletePhoto() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: { message: "Confirmez-vous la suppression de la photo ?" },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDeletePhoto();
        }
      });
    }
  }

  doDeletePhoto() {
    this.deleteSub = this.photoService
      .deletePhoto(this.photo.uid)
      .subscribe((r) => {
        this.photoUpdated.emit(true);
        this.alertService.success("Suppression effectuée avec succès");
      });
  }

  confirmSetAsMainPhoto() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: { message: "Confirmez-vous le changement de photo ?" },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doSetAsMainPhoto();
        }
      });
    }
  }

  doSetAsMainPhoto() {
    this.setMainSub = this.photoService
      .setMainPhoto(this.photo.uid)
      .subscribe(() => {
        this.photoUpdated.emit(true);
        this.mainPhotoUpdated.emit(true);
        this.alertService.success("Photo principale définie");
      });
  }

  changeOrder() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ReOrderDialogComponent, {
        // width: '25rem',
        data: {
          currentOrder: this.order,
          titleSuffix: "photo",
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doChangeOrder(this.photo, result);
        }
      });
    }
  }

  moveOrderUp() {
    this.doChangeOrder(this.photo, this.order - 1);
  }

  moveOrderDown() {
    this.doChangeOrder(this.photo, this.order + 1);
  }

  doChangeOrder(photo: Photo, newOrder: number) {
    this.changeOrderSub = this.photoService
      .updatePhotoOrder(photo.uid, newOrder)
      .subscribe(() => {
        this.alertService.success("Modification effectuée avec succès");
        this.photoUpdated.emit(true);
      });
  }
}
